import { Button } from '@getgo/g2r-shared-components';
import * as React from 'react';
import Loader from 'react-loader-spinner';
import Select from 'react-select';
import { AccountProps } from '../../contexts/SettingsKeeper';
import { polyglot } from '../../helpers/Polyglot';
import { LocalesKeys } from '../../localesKeys';
import { Logo } from '../Logo/Logo';
import { PinInputs } from '../PinInputs/PinInputs';
import styles from './ActivationForm.scss';

export interface ActivationActionProps {
    activationCode: string;
    activateDevice: (a) => void;
    setActivationCode: (a) => void;
    setCurrentAccountKey: (a) => void;
    hasLicense: boolean;
    loading: boolean;
    seatAvailable: boolean;
    userHasAccess: boolean;
    errorCode: string;
    setActivationStatus: (a) => void;
}

export interface ActivationFormProps extends ActivationActionProps {
    accounts: AccountProps[];
    currentAccountKey: string;
    activationStatus: string;
}

export const ActivationForm = ({accounts, currentAccountKey, activationStatus,  ...accountDetails}: ActivationFormProps) => {
    const { setCurrentAccountKey, activateDevice,  activationCode, setActivationCode, loading, errorCode, setActivationStatus} = { ...accountDetails };
    const [validCode, setValidCode] = React.useState(true);

    const handleChange = (acc) => {
        setCurrentAccountKey(acc.value);
    };

    React.useEffect(() => {
            switch (errorCode) {
                case 'devicetoken.not.found':
                case 'devicetoken.registered':
                case 'devicetoken.expired':
                    setValidCode(false);
                    break;

                default:
                    if (errorCode) {
                        throw new Error(errorCode);
                    }
                    break;
            }
    }, [errorCode]);

    const customStyles = {
        control: () => ({
            alignItems: 'center',
            backgroundColor: 'hsl(0,0%,100%)',
            borderColor: 'hsl(0,0%,80%)',
            borderRadius: '4px',
            border: 'solid 1px rgba(76, 83, 93, 0.24)',
            cursor: 'default',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            minHeight: '38px',
            outline: '0 !important',
            position: 'relative',
            transition: 'all 100ms',
            boxSizing: 'border-box',
        }),
    };

    const userAccounts = () => {
        if (accounts.length > 1) {
            const options = accounts.reduce((acc, currentAcc) => {
                acc.push({id: currentAcc.key, value: currentAcc.key, label: currentAcc.name});
                return acc;
            }, []);
            return(<>
                    <div>{polyglot.t(LocalesKeys.activationFormDescription)}.</div>
                    <div className={styles.AddDeviceToOne}>{polyglot.t(LocalesKeys.selectYourAccount)}</div>
                    <div><Select
                            id='accountList'
                            className={styles.AccountSelect}
                            options={options}
                            defaultValue={{ label: accounts[0].name, value: accounts[0].key }}
                            // placeholder={polyglot.t(LocalesKeys.selectAccount)}
                            components={{ IndicatorSeparator: () => '' }}
                            onChange={handleChange}
                            styles={customStyles}
                          />
                    </div>
            </>);
        } else {
            return <div>{polyglot.t(LocalesKeys.activationFormDescription)}: {accounts[0].name}.</div> ;
        }
    };

    const loaderIcon = () => loading ? <div className={styles.Loading}><Loader type={'TailSpin'} color={'#FFFFFF'} height={35} width={35} /></div> : '';

    const buttonDisabled =  (loading || !activationCode || activationCode.length !== 6);

    const hasValidCode = () => {
        if (activationCode && activationCode.length === 6 && currentAccountKey) {
            activateDevice(activationCode);
        } else {
            setValidCode(false);
        }
    };

    const updateCode = (code) => {
        setValidCode(true);
        setActivationCode(code);
    };

    const handleKeyDown = (key) => {
        if (validCode && activationCode.length === 6 && key.key === 'Enter') {
            hasValidCode();
        }
    };

    return(<div className={styles.Activation}>
                <div className={styles.Header}><Logo /></div>
                <div id='title' className={styles.Title}>{polyglot.t(LocalesKeys.activationFormTitle)}</div>
                <div className={styles.Content}>{ userAccounts()}</div>
                <div className={styles.Content} onKeyDown={handleKeyDown}>
                    <div className={styles.DeviceCode}>{polyglot.t(LocalesKeys.deviceCode)}</div>
                    <PinInputs setCode={updateCode} activationStatus={activationStatus} setActivationStatus={setActivationStatus} inputError={!validCode}/>
                </div>
                <div className={styles.ActionButton}>
                    <Button id='pairDevice' disabled={buttonDisabled} onClick={() => hasValidCode()}>
                        <span className={styles.ButtonText}>{polyglot.t(LocalesKeys.addDevice)}</span>
                        {loaderIcon()}
                    </Button>
                </div>
                <div className={[styles.WrapperFooter, styles.FlexContentRight].join(' ')}>
                  <a href='https://support.goto.com/meeting/help/how-do-i-activate-my-gotoroom-device' className={styles.ExternalLink}>{polyglot.t(LocalesKeys.support)}</a>
                </div>
        </div>);
};
